import { useState } from "react";

import dayjs, { type Dayjs } from "dayjs";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";

import Button from "@/components/Button";
import { daysOfTheWeek } from "@/constants/calendar";
import type { VehicleReservation } from "@/types/vehicleReservation";
import { getWeeks } from "@/utils/calendar";
import { cn } from "@/utils/cn";
import { hasAllDayReservation } from "@/utils/vehicleAvailability";

interface VehicleCalendarProps {
  reservations: VehicleReservation[]; // 선택 차량으로 필터된 예약
  selectedDate: Dayjs | null;
  onSelectDate: (date: Dayjs) => void;
  onMonthChange?: (month: Dayjs) => void;
  unlimited?: boolean; // 관리자: 다음달 말 상한 없이 미래 날짜 선택 허용
}

const CalendarHead = () => (
  <div className="grid grid-cols-7 text-center text-small font-medium">
    {daysOfTheWeek.map((day, i) => (
      <div
        key={day}
        className={cn(
          "py-2",
          i === 0 && "text-red",
          i === 6 && "text-saturday",
        )}
      >
        {day}
      </div>
    ))}
  </div>
);

const VehicleCalendar = ({
  reservations,
  selectedDate,
  onSelectDate,
  onMonthChange,
  unlimited = false,
}: VehicleCalendarProps) => {
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(dayjs().date(1));

  const today = dayjs();
  const maxDate = dayjs().add(1, "month").endOf("month");

  const goPreviousMonth = () => {
    const newMonth = firstDayOfMonth.subtract(1, "month").date(1);
    setFirstDayOfMonth(newMonth);
    onMonthChange?.(newMonth);
  };
  const goNextMonth = () => {
    const newMonth = firstDayOfMonth.add(1, "month").date(1);
    setFirstDayOfMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  const getReservationsForDate = (date: Dayjs) =>
    reservations.filter(
      r =>
        r.reservationDate === date.format("YYYY-MM-DD") &&
        (r.status === "approved" || r.status === "pending"),
    );

  // 선택 차량이 그날 전일 예약을 갖고 있는지
  const isFullyBlocked = (date: Dayjs) =>
    hasAllDayReservation(getReservationsForDate(date));

  const isSelectable = (date: Dayjs) => {
    if (date.isBefore(today, "day")) return false;
    if (!unlimited && date.isAfter(maxDate, "day")) return false;
    return true;
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Header - month navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="custom" onClick={goPreviousMonth}>
          <SlArrowLeft size={10} />
        </Button>
        <span className="min-w-[120px] text-center text-base font-medium">
          {firstDayOfMonth.format("YYYY. MM")}
        </span>
        <Button variant="custom" onClick={goNextMonth}>
          <SlArrowRight size={10} />
        </Button>
      </div>

      {/* Calendar grid */}
      <CalendarHead />
      {getWeeks(firstDayOfMonth).map(week => (
        <div key={week[0].valueOf()} className="grid grid-cols-7 text-center">
          {week.map(day => {
            const isCurrentMonth = day.isSame(firstDayOfMonth, "month");
            const dateReservations = getReservationsForDate(day);
            const resCount = dateReservations.length;
            const isAllDay = isFullyBlocked(day);
            const selectable = isSelectable(day) && isCurrentMonth;
            const isSelected = selectedDate && day.isSame(selectedDate, "day");
            const isToday = day.isSame(today, "day");
            const isPast = day.isBefore(today, "day");
            const isFutureBeyondMax = !unlimited && day.isAfter(maxDate, "day");
            const isOutOfRange = isPast || isFutureBeyondMax;

            return (
              <div
                key={day.valueOf()}
                className={cn(
                  "relative flex min-h-[46px] flex-col items-center justify-center py-1.5 text-small",
                  !isCurrentMonth && "opacity-30",
                  // 전일 예약: 빨간 배경
                  isAllDay &&
                    isCurrentMonth &&
                    "cursor-pointer rounded-[5px] bg-red/10",
                  // 시간 예약 있는 날: 연한 배경 + 클릭 가능
                  resCount > 0 &&
                    !isAllDay &&
                    isCurrentMonth &&
                    "cursor-pointer rounded-[5px] bg-green-50",
                  // 예약 없는 날: 선택 가능
                  resCount === 0 &&
                    selectable &&
                    "cursor-pointer hover:bg-gray-light/50",
                  // 예약 불가 기간 (과거 또는 다음달 이후)
                  resCount === 0 &&
                    !selectable &&
                    isCurrentMonth &&
                    isOutOfRange &&
                    "cursor-not-allowed bg-gray-light/30 text-gray-dull/60 line-through",
                  // 선택된 날짜
                  isSelected &&
                    "rounded-[5px] border-2 border-primary bg-primary-light/[.34]",
                  !isSelected && "border border-transparent",
                  // 오늘
                  isToday &&
                    !isSelected &&
                    resCount === 0 &&
                    "font-bold text-primary",
                  // 주말
                  day.day() === 0 && isCurrentMonth && selectable && "text-red",
                  day.day() === 6 &&
                    isCurrentMonth &&
                    selectable &&
                    "text-saturday",
                )}
                onClick={() => {
                  if (isCurrentMonth && (selectable || resCount > 0))
                    onSelectDate(day);
                }}
                title={
                  isCurrentMonth && isOutOfRange && resCount === 0
                    ? isPast
                      ? "지난 날짜는 예약할 수 없습니다"
                      : "예약 가능 기간을 벗어났습니다 (다음달까지 가능)"
                    : undefined
                }
              >
                <span
                  className={cn(
                    isAllDay && isCurrentMonth && "font-semibold text-red",
                    resCount > 0 &&
                      !isAllDay &&
                      isCurrentMonth &&
                      "font-semibold text-green-700",
                  )}
                >
                  {day.date()}
                </span>
                {/* 전일 예약: 예약불가 */}
                {isAllDay && isCurrentMonth && (
                  <span className="mt-0.5 text-[9px] font-medium leading-none text-red">
                    예약불가
                  </span>
                )}
                {/* 시간 예약: 건수 표시 */}
                {resCount > 0 && !isAllDay && isCurrentMonth && (
                  <span className="mt-0.5 text-[9px] font-medium leading-none text-green-600">
                    {resCount}건
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-small text-gray-dull">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-red/10 px-1 text-[9px] font-semibold text-red">
            예약불가
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-green-50 px-1 text-[9px] font-semibold text-green-600">
            N건
          </span>
          <span>시간 예약</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full border border-gray-middle bg-white" />
          <span>예약 가능</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="rounded bg-gray-light/50 px-1 text-[9px] font-medium text-gray-dull/70 line-through">
            00
          </span>
          <span>예약 불가</span>
        </div>
      </div>
    </div>
  );
};

export default VehicleCalendar;
